import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  PiPlusLight,
  PiPencilSimpleLineLight,
  PiTrashLight,
  PiClockLight,
  PiCaretLeftLight,
  PiCaretRightLight,
  PiFunnelLight,
  PiListBulletsLight,
} from 'react-icons/pi';
import { RootState, useAppDispatch } from '../../../store';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import { showSidePanel, hideSidePanel } from '../../../store/actions/sidePanelActions';
import {
  fetchActivityTypes,
  fetchTimeEntries,
  createTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
  TimeEntryFilters,
  TimeEntryWritePayload,
} from '../../../store/actions/economicsActions';
import Button from '../../shared/TailwindComponents/Button';
import Table from '../../shared/TailwindComponents/Table';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import DateTimePicker from '../../shared/TailwindComponents/DatePicker';
import Badge from '../../shared/TailwindComponents/Badge';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import GlassCard from '../../shared/brand/GlassCard';
import { KpiTile } from '../../shared/brand/Field';
import clsx from 'clsx';
import { ymdToDate, dateToYmd } from './dateHelpers';
import UserCell, { UserCellUser } from './UserCell';
import { useMatterCollaborators } from '../../../hooks/useMatterCollaborators';
import type { MatterData } from '../MatterDetails';
import type { ActivityType, TimeEntry } from '../../../types/economics';

interface MatterTimeTabProps {
  matterData: MatterData | null;
  userAccessLevel: 'edit' | 'admin' | 'view';
}

const fmtDate = (d: Date) => d.toISOString().slice(0, 10);

/** Monday of the week containing `date`. */
const weekStart = (date: Date): Date => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date: Date, n: number): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

const WEEKDAY_KEYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Per-matter time entries (US #2237). List view (task #2266) with filters,
 * inline edit/delete and a total; plus a read-only weekly grid (task #2267).
 */
const MatterTimeTab: React.FC<MatterTimeTabProps> = ({ matterData, userAccessLevel }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const currentUserId = userInfo?.UserID;
  const currentUserUuid = userInfo?.UserUID;
  const isAdmin = userAccessLevel === 'admin' || !!userInfo?.is_org_admin;

  const matterUuid = matterData?.uuid || '';

  const [view, setView] = useState<'list' | 'weekly'>('list');
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState<TimeEntryFilters>({});
  const [weekAnchor, setWeekAnchor] = useState<Date>(() => weekStart(new Date()));

  // Matter people (collaborators + owner) via the shared hook (FIX 2).
  const matterPeople = useMatterCollaborators(matterData);
  const { collaboratorName, collaboratorOptions } = useMemo(() => {
    // Picker options bind to user_uuid; the name map is keyed by uuid AND numeric
    // id so rows that only carry one of them still resolve a label.
    const nameById = new Map<string, string>();
    const options = matterPeople.map((c) => {
      const label = c.isOwner ? `${c.name} (${t('Owner')})` : c.name;
      if (c.user_uuid) nameById.set(c.user_uuid, label);
      if (c.user_id) nameById.set(String(c.user_id), label);
      return { value: c.user_uuid, label };
    });
    return {
      collaboratorName: (id: number | string) => nameById.get(String(id)) || `User ${id}`,
      collaboratorOptions: options,
    };
  }, [matterPeople, t]);

  const activityLabel = useMemo(() => {
    const byId = new Map<number, string>();
    activityTypes.forEach((a) => byId.set(a.id, a.label));
    return (id?: number | null) => (id != null && byId.has(id) ? t(byId.get(id)!) : '—');
  }, [activityTypes, t]);

  useEffect(() => {
    dispatch(fetchActivityTypes()).then(setActivityTypes);
  }, [dispatch]);

  // List view uses the filter state; weekly view fetches its own week window.
  const activeFilters = useMemo<TimeEntryFilters>(() => {
    if (view === 'weekly') {
      return { date_from: fmtDate(weekAnchor), date_to: fmtDate(addDays(weekAnchor, 6)) };
    }
    return filters;
  }, [view, weekAnchor, filters]);

  const load = useCallback(async () => {
    if (!matterUuid) return;
    setLoading(true);
    setError(false);
    try {
      const data = await dispatch(fetchTimeEntries(matterUuid, activeFilters));
      setEntries(Array.isArray(data) ? data : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [dispatch, matterUuid, activeFilters]);

  useEffect(() => {
    load();
  }, [load]);

  const canManage = (entry: TimeEntry) =>
    isAdmin || String(entry.user_id) === String(currentUserId);

  const openEditor = (timeEntry: TimeEntry | null) => {
    dispatch(
      showModal({
        showModal: true,
        type: 'editTimeEntry',
        message: '',
        title: timeEntry ? t('Edit time entry') : t('Add time entry'),
        timeEntry,
        activityTypes,
        collaborators: collaboratorOptions,
        defaultUserUuid: currentUserUuid ? String(currentUserUuid) : undefined,
        onSave: async (payload: TimeEntryWritePayload) => {
          if (timeEntry) await dispatch(updateTimeEntry(matterUuid, timeEntry.id, payload));
          else await dispatch(createTimeEntry(matterUuid, payload));
        },
        onAfterSave: load,
      }),
    );
  };

  /**
   * A row's own surface (mat-16). Clicking a row did nothing at all, so an
   * entry could only be read through the edit form. The panel carries the
   * reading, the comments and the edit; delete stays here, because the
   * confirmation and the reload belong to the list.
   */
  const openEntryPanel = (entry: TimeEntry) => {
    dispatch(
      showSidePanel({
        type: 'matterTimeEntry',
        showSidePanel: true,
        size: 'sm',
        message: t('Time entry'),
        icon: <PiClockLight className="w-6 h-6 text-gray-600" />,
        entry,
        matterUuid,
        activityTypes,
        fallbackLabel: collaboratorName(entry.user_id),
        canManage: canManage(entry),
        onSaved: load,
        onDeleteEntry: (e: TimeEntry) => confirmDelete(e),
        onCancel: () => dispatch(hideSidePanel()),
      }),
    );
  };

  const confirmDelete = (entry: TimeEntry) => {
    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Delete time entry'),
        subMsg: t('This action cannot be undone.'),
        icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
        isDest: true,
        onConfirm: async () => {
          dispatch(hideModal());
          try {
            await dispatch(deleteTimeEntry(matterUuid, entry.id));
            await load();
          } catch {
            /* toast shown by thunk */
          }
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  };

  // ── List view ───────────────────────────────────────────────────────────────
  const columns = [t('Date'), t('Collaborator'), t('Activity'), t('Hours'), t('Billable'), t('Description')];
  const rows = entries.map((e) => ({
    id: e.id,
    [t('Date')]: e.entry_date,
    [t('Collaborator')]: e,
    [t('Activity')]: activityLabel(e.activity_type_id),
    [t('Hours')]: Number(e.hours_decimal).toFixed(2),
    [t('Billable')]: e.billable,
    [t('Description')]: e.description || '—',
    _raw: e,
  }));

  const totalHours = entries.reduce((s, e) => s + Number(e.hours_decimal || 0), 0);
  const billableHours = entries.filter((e) => e.billable).reduce((s, e) => s + Number(e.hours_decimal || 0), 0);

  const renderCell = (column: string, value: any) => {
    if (column === t('Collaborator')) {
      const e = value as TimeEntry;
      return <UserCell user={e} fallbackLabel={collaboratorName(e.user_id)} />;
    }
    if (column === t('Billable')) {
      return value
        ? <Badge color="green">{t('Billable')}</Badge>
        : <Badge color="gray">{t('Non-billable')}</Badge>;
    }
    return undefined;
  };

  const actions = [
    {
      label: t('Edit'),
      icon: <PiPencilSimpleLineLight />,
      hidden: (row: any) => !canManage(row._raw as TimeEntry),
      onClick: (row: any) => openEditor(row._raw as TimeEntry),
    },
    {
      label: t('Delete'),
      icon: <PiTrashLight />,
      isDestructive: true,
      hidden: (row: any) => !canManage(row._raw as TimeEntry),
      onClick: (row: any) => confirmDelete(row._raw as TimeEntry),
    },
  ];

  // ── Weekly grid (read-only) ──────────────────────────────────────────────────
  const weekDays = useMemo(() => WEEKDAY_KEYS.map((k, i) => ({ key: k, date: fmtDate(addDays(weekAnchor, i)) })), [weekAnchor]);
  /**
   * The grid's rows come from the ENTRIES, not from the collaborator list.
   *
   * Iterating collaborators hid every entry logged by someone who is not (or
   * is no longer) on the matter, and rendered "no collaborators" — i.e. no
   * data — whenever the roster came back empty even though the week was full
   * of hours. The roster is still merged in, so people with a quiet week keep
   * their row; anyone with hours gets one regardless.
   */
  const weeklyRows = useMemo(() => {
    type Row = {
      key: string;
      user: UserCellUser;
      fallbackLabel: string;
      days: Map<string, number>;
    };
    const rowsByUser = new Map<string, Row>();

    // Roster first, so on-matter people keep their usual order at the top.
    matterPeople.forEach((c) => {
      const key = String(c.user_uuid || c.user_id || '');
      if (!key) return;
      rowsByUser.set(key, {
        key,
        user: { name: c.name, email: c.email, avatar_url: c.avatar, user_id: c.user_id },
        fallbackLabel: c.name,
        days: new Map(),
      });
    });

    entries.forEach((e) => {
      // Key by uuid (matches the collaborator hook); fall back to numeric id.
      const key = String(e.user_uuid || e.user_id);
      let row = rowsByUser.get(key);
      if (!row) {
        row = {
          key,
          user: { name: e.name, email: e.email, avatar_url: e.avatar_url, user_id: e.user_id },
          fallbackLabel: collaboratorName(e.user_id),
          days: new Map(),
        };
        rowsByUser.set(key, row);
      }
      row.days.set(e.entry_date, (row.days.get(e.entry_date) || 0) + Number(e.hours_decimal || 0));
    });

    return Array.from(rowsByUser.values());
  }, [entries, matterPeople, collaboratorName]);

  /**
   * The List / Weekly switch.
   *
   * It used to sit in its own row hard under the grey sub-tab bar, where it
   * read as part of the navigation chrome rather than as a control over the
   * content. It now rides the header of whichever card it governs — the
   * filters in the list, the grid in the weekly view (mat-16).
   */
  const viewToggle = (
    <div
      role="tablist"
      aria-label={t('Time')}
      className="glass-bar inline-flex items-center gap-0.5 rounded-full p-0.5
                 border border-hairline-soft shadow-sm"
    >
      {(['list', 'weekly'] as const).map((mode) => (
        <button
          key={mode}
          type="button"
          role="tab"
          aria-selected={view === mode}
          onClick={() => setView(mode)}
          className={clsx(
            'nav-pill rounded-full px-3 py-1 text-xs font-medium',
            'transition-colors duration-200 ease-slide',
            view === mode ? 'nav-pill-active text-fg-strong' : 'text-fg-muted hover:text-fg-body'
          )}
        >
          {mode === 'list' ? t('List') : t('Weekly')}
        </button>
      ))}
    </div>
  );

  const addButton = (
    <Button variant="primary" size="small" icon={<PiPlusLight />} onClick={() => openEditor(null)} disabled={!matterUuid}>
      {t('Add time entry')}
    </Button>
  );

  return (
    <div className="space-y-4">
      {view === 'list' ? (
        <>
          {/* Filters BESIDE the totals (mat-16). Five filters spanning the
              whole width pushed the table below the fold and left the row
              under them half empty; the two containers now share one row and
              stretch to the same height. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <GlassCard fill icon={<PiFunnelLight />} title={t('Filters')} actions={viewToggle} padding="md">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Dropdown
              id="time-filter-user"
              label={t('Collaborator')}
              options={[{ value: '', label: t('All collaborators') }, ...collaboratorOptions]}
              value={filters.user_id ? String(filters.user_id) : ''}
              placeholder={t('All collaborators')}
              onChange={(v) => setFilters((p) => ({ ...p, user_id: (Array.isArray(v) ? v[0] : v) || undefined }))}
              fullWidth
            />
            <Dropdown
              id="time-filter-activity"
              label={t('Activity')}
              options={[{ value: '', label: t('All activities') }, ...activityTypes.map((a) => ({ value: String(a.id), label: t(a.label) }))]}
              value={filters.activity_type_id ? String(filters.activity_type_id) : ''}
              placeholder={t('All activities')}
              onChange={(v) => setFilters((p) => ({ ...p, activity_type_id: (Array.isArray(v) ? v[0] : v) || undefined }))}
              fullWidth
            />
            <DateTimePicker
              id="time-filter-from"
              label={t('From')}
              selected={ymdToDate(filters.date_from)}
              placeholder={t('From')}
              showTimeSelect={false}
              dateFormat="yyyy-MM-dd"
              fullWidth
              onChange={(d) => setFilters((p) => ({ ...p, date_from: dateToYmd(d) || undefined }))}
            />
            <DateTimePicker
              id="time-filter-to"
              label={t('To')}
              selected={ymdToDate(filters.date_to)}
              placeholder={t('To')}
              showTimeSelect={false}
              dateFormat="yyyy-MM-dd"
              fullWidth
              onChange={(d) => setFilters((p) => ({ ...p, date_to: dateToYmd(d) || undefined }))}
            />
            <Dropdown
              id="time-filter-billable"
              label={t('Billable')}
              options={[
                { value: '', label: t('All') },
                { value: 'true', label: t('Billable') },
                { value: 'false', label: t('Non-billable') },
              ]}
              value={filters.billable === undefined ? '' : String(filters.billable)}
              placeholder={t('All')}
              onChange={(v) => {
                const sel = Array.isArray(v) ? v[0] : v;
                setFilters((p) => ({ ...p, billable: sel === '' ? undefined : sel === 'true' }));
              }}
              fullWidth
            />
          </div>
          </GlassCard>

          {/* The totals the filters produce, on the same row as the filters
              that produced them — and the place the Add action lives. */}
          <GlassCard
            fill
            padding="none"
            icon={<PiClockLight />}
            title={t('Totals')}
            actions={addButton}
            bodyClassName="justify-center"
          >
            <div className="grid grid-cols-3 divide-x divide-hairline-soft">
              <KpiTile
                icon={<PiClockLight />}
                label={t('Total hours')}
                value={totalHours.toFixed(2)}
              />
              <KpiTile
                icon={<PiClockLight />}
                label={t('Billable hours')}
                value={billableHours.toFixed(2)}
              />
              <KpiTile
                icon={<PiListBulletsLight />}
                label={t('Entries')}
                value={entries.length}
              />
            </div>
          </GlassCard>
          </div>

          {error ? (
            <div className="text-sm text-fg-muted py-8 text-center">
              {t('Could not load time entries.')}{' '}
              <button className="text-primary-600 underline" onClick={load}>{t('Retry')}</button>
            </div>
          ) : (
            <GlassCard padding="none">
              <Table
                isLoading={loading && entries.length === 0}
                columns={columns}
                data={rows}
                actions={actions}
                renderCell={renderCell}
                // Clicking a row opens the entry's own panel (mat-16); the
                // kebab keeps the direct edit/delete for people who know it.
                onRowClick={(row: any) => openEntryPanel(row._raw as TimeEntry)}
                isLoadingMore={loading}
                min="min-h-[6rem]"
                max="max-h-[calc(100vh-360px)]"
                emptyState={{
                  icon: <PiClockLight className="h-6 w-6" />,
                  headline: t('No time entries'),
                  subtext: t('Add a time entry to start tracking work on this matter.'),
                  button: { label: t('Add time entry'), onClick: () => openEditor(null) },
                }}
              />
            </GlassCard>
          )}
        </>
      ) : (
        <>
          {loading && entries.length === 0 ? (
            <div className="space-y-4">
              {/* The switch survives the wait — a week that takes a moment to
                  arrive must not trap the user in the weekly view. */}
              <div className="flex items-center justify-between gap-3">
                {viewToggle}
                {addButton}
              </div>
              <Skeleton variant="weekly" rows={4} />
            </div>
          ) : (
          <div>
            {/* The week selector is the grid's header, not a control bar
                hovering above it — same glass, one object. */}
            <GlassCard
              padding="none"
              icon={<PiClockLight />}
              title={`${fmtDate(weekAnchor)} – ${fmtDate(addDays(weekAnchor, 6))}`}
              actions={
                <>
                  {viewToggle}
                  {/* WHICH week, not just the next one: prev/next walk, the
                      picker jumps, and any day picked snaps to its Monday
                      (mat-16). */}
                  <div className="w-36">
                    <DateTimePicker
                      id="time-week-picker"
                      selected={weekAnchor}
                      placeholder={t('Pick a week')}
                      showTimeSelect={false}
                      dateFormat="yyyy-MM-dd"
                      fullWidth
                      onChange={(d) => { if (d) setWeekAnchor(weekStart(d as Date)); }}
                    />
                  </div>
                  <Button variant="neutral" size="small" ariaLabel={t('Previous week')} icon={<PiCaretLeftLight />} onClick={() => setWeekAnchor((d) => addDays(d, -7))} />
                  <Button variant="neutral" size="small" ariaLabel={t('Next week')} icon={<PiCaretRightLight />} onClick={() => setWeekAnchor((d) => addDays(d, 7))} />
                  <Button variant="outline" size="small" onClick={() => setWeekAnchor(weekStart(new Date()))}>
                    {t('This week')}
                  </Button>
                  {addButton}
                </>
              }
            >
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-hairline-soft">
                      <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-fg-muted">
                        {t('Collaborator')}
                      </th>
                      {weekDays.map((d) => (
                        <th key={d.key} className="px-3 py-2 text-center text-[11px] font-medium text-fg-muted">
                          {t(d.key)}
                          <br />
                          <span className="text-[10px] font-normal tabular-nums">{d.date.slice(5)}</span>
                        </th>
                      ))}
                      <th className="px-3 py-2 text-center text-[11px] font-medium uppercase tracking-wider text-fg-muted">
                        {t('Total')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-hairline-soft">
                    {weeklyRows.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-3 py-8 text-center text-sm text-fg-muted">
                          {t('No time logged this week.')}
                        </td>
                      </tr>
                    ) : (
                      weeklyRows.map((row) => {
                        const dayMap = row.days;
                        const rowTotal = weekDays.reduce((sum, d) => sum + (dayMap.get(d.date) || 0), 0);
                        return (
                          <tr key={row.key} className="transition-colors duration-200 ease-slide hover:bg-surface/60">
                            <td className="px-3 py-2">
                              <UserCell user={row.user} fallbackLabel={row.fallbackLabel} />
                            </td>
                            {weekDays.map((d) => {
                              const h = dayMap.get(d.date) || 0;
                              return (
                                <td
                                  key={d.key}
                                  className={clsx(
                                    'px-3 py-2 text-center tabular-nums',
                                    h ? 'text-fg-body' : 'text-fg-muted/50'
                                  )}
                                >
                                  {h ? h.toFixed(2) : '·'}
                                </td>
                              );
                            })}
                            <td className="px-3 py-2 text-center tabular-nums font-medium text-fg-strong">
                              {rowTotal.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </GlassCard>
          </div>
          )}
        </>
      )}
    </div>
  );
};

export default MatterTimeTab;
